import React, { useCallback, useEffect, useRef, useState } from 'react'
import { DateInput } from '../../../../common/components/DateInput'
import { Pagination } from '../../../../common/components/Pagination'
import { TabSkeleton } from '../../../../common/components/TabSkeleton'
import { UnsavedChangesModal } from '../../../../common/components/UnsavedChangesModal'
import { BackofficePermissionsEnum as Permissions } from '../../../../common/enums/backOfficePermissionsEnum'
import usePermissions from '../../../../common/hooks/usePermissions'
import { useUnsavedChanges } from '../../../../common/hooks/useUnsavedChanges'
import { customSwal } from '../../../../config/swal'
import {
  FundDraftService,
  FundSeriesTab,
  IQuoteValueSerieItem,
  IQuoteValueSeriePaginatedResponse,
} from '../../../../services/FundDraftService'
import { useFundSeries } from '../hooks/useFundSeries'

interface IProps {
  fundId: string
  onDirtyChange?: (dirty: boolean) => void
  saveRef?: React.MutableRefObject<(() => Promise<void>) | null>
}

const MAX_TWO_DECIMALS_REGEX = /^-?\d*(\.\d{0,2})?$/

export const QuoteValueTab: React.FC<IProps> = ({ fundId, onDirtyChange, saveRef }) => {
  const { hasPermission } = usePermissions()
  const { data: seriesData, isLoading, refetchSeriesOnly } = useFundSeries(fundId)

  const [activeSerieCode, setActiveSerieCode] = useState('')
  const [quoteValueAction, setQuoteValueAction] = useState<'saving' | 'publishing' | null>(null)

  const [visibility, setVisibility] = useState(true)
  const [title, setTitle] = useState('')
  const [yAxisMin, setYAxisMin] = useState<number | string>('')
  const [yAxisMax, setYAxisMax] = useState<number | string>('')
  const [yAxisInterval, setYAxisInterval] = useState<number | string>('')
  const [yAxisStrict, setYAxisStrict] = useState(false)
  const [evolutionChips, setEvolutionChips] = useState<string[]>([])

  const [isDirty, setIsDirty] = useState(false)
  const [chipEditMode, setChipEditMode] = useState(false)
  const [chipSnapshot, setChipSnapshot] = useState<string[]>([])
  const dirtyBeforeChipEdit = useRef(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [modalFilter, setModalFilter] = useState('')
  const [modalPage, setModalPage] = useState(1)
  const [modalTotalPages, setModalTotalPages] = useState(1)
  const [modalRows, setModalRows] = useState<IQuoteValueSerieItem[]>([])
  const [modalLoading, setModalLoading] = useState(false)
  const [modalDateSearch, setModalDateSearch] = useState('')

  const initialized = useRef(false)
  const skipDirtyCount = useRef(1)

  const applySerieData = (code: string) => {
    if (!seriesData) return
    skipDirtyCount.current += 1
    const qv = seriesData.tabs.quote_value[code]
    setVisibility(qv?.visibility ?? true)
    setTitle(qv?.title ?? '')
    setYAxisMin(qv?.yAxis?.start ?? '')
    setYAxisMax(qv?.yAxis?.end ?? '')
    setYAxisInterval(qv?.yAxis?.interval ?? '')
    setYAxisStrict(qv?.yAxis?.strict ?? false)
    setEvolutionChips(qv?.evolution ?? [])
  }

  useEffect(() => {
    if (!seriesData) return
    if (initialized.current) {
      if (activeSerieCode) applySerieData(activeSerieCode)
      return
    }
    const firstCode = seriesData.series[0]?.serie_codigo
    if (!firstCode) return
    initialized.current = true
    setActiveSerieCode(firstCode)
    applySerieData(firstCode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesData])

  const fetchModalData = useCallback(
    (page: number, filter: string, dateSearch: string) => {
      if (!activeSerieCode) return
      setModalLoading(true)
      FundDraftService()
        .getQuoteValuesSerie(fundId, activeSerieCode, {
          page,
          limit: 10,
          filter: filter || undefined,
          ...(dateSearch.length === 10 ? { date: dateSearch.split('/').reverse().join('-') } : {}),
        })
        .then((res: IQuoteValueSeriePaginatedResponse) => {
          setModalRows(res.data)
          setModalTotalPages(res.totalPages)
        })
        .catch(() => setModalRows([]))
        .finally(() => setModalLoading(false))
    },
    [fundId, activeSerieCode]
  )

  const openChipModal = (chip: string) => {
    setModalFilter(chip)
    setModalPage(1)
    setModalDateSearch('')
    setModalOpen(true)
    fetchModalData(1, chip, '')
  }

  useEffect(() => {
    if (modalOpen) fetchModalData(modalPage, modalFilter, modalDateSearch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalPage])

  const buildPayload = () => ({
    visibility,
    title,
    yAxis: {
      start: yAxisMin !== '' ? Number(yAxisMin) : undefined,
      end: yAxisMax !== '' ? Number(yAxisMax) : undefined,
      interval: yAxisInterval !== '' ? Number(yAxisInterval) : undefined,
      strict: yAxisStrict,
    },
    evolution: evolutionChips.map((c) => c.trim()).filter((c) => c !== ''),
  })

  const handleSave = async () => {
    setQuoteValueAction('saving')
    try {
      await FundDraftService().updateSeriesTab(
        fundId,
        activeSerieCode,
        FundSeriesTab.QUOTE_VALUE,
        buildPayload()
      )
      setIsDirty(false)
      refetchSeriesOnly()
      customSwal.fire({ title: 'Desempeño guardado correctamente', icon: 'success' })
    } catch {
      customSwal.fire({ title: 'Error al guardar el desempeño', icon: 'error' })
    }
    setQuoteValueAction(null)
  }

  const handlePublish = async () => {
    setQuoteValueAction('publishing')
    try {
      await FundDraftService().updateSeriesTab(
        fundId,
        activeSerieCode,
        FundSeriesTab.QUOTE_VALUE,
        buildPayload()
      )
      await FundDraftService().publishSeriesTab(fundId, activeSerieCode, FundSeriesTab.QUOTE_VALUE)
      setIsDirty(false)
      refetchSeriesOnly()
      customSwal.fire({ title: 'Desempeño publicado correctamente', icon: 'success' })
    } catch {
      customSwal.fire({ title: 'Error al publicar el desempeño', icon: 'error' })
    }
    setQuoteValueAction(null)
  }

  const { guardNavigation, isModalOpen, handleLeave, handleCancel, handleSaveAndContinue, saving } =
    useUnsavedChanges(isDirty, handleSave)

  const handleSerieChange = (code: string) => {
    guardNavigation(() => {
      setIsDirty(false)
      setChipEditMode(false)
      setActiveSerieCode(code)
      applySerieData(code)
    })
  }

  // Mark dirty when any user-editable state changes (skip load-triggered fires)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (skipDirtyCount.current > 0) {
      skipDirtyCount.current -= 1
      return
    }
    setIsDirty(true)
  }, [visibility, title, yAxisMin, yAxisMax, yAxisInterval, yAxisStrict, evolutionChips])

  // Report dirty state to ConfigFunds
  useEffect(() => {
    onDirtyChange?.(isDirty)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty])

  // Keep saveRef current
  useEffect(() => {
    if (saveRef) saveRef.current = handleSave
  })

  // Chip management
  const enterChipEditMode = () => {
    dirtyBeforeChipEdit.current = isDirty
    setChipSnapshot([...evolutionChips])
    setChipEditMode(true)
  }
  const confirmChipEdit = () => {
    const validChips = evolutionChips.map((c) => c.trim()).filter((c) => c !== '')
    if (new Set(validChips).size !== validChips.length) {
      customSwal.fire({ title: 'Hay chips con nombre duplicado', icon: 'warning' })
      return
    }
    setEvolutionChips(validChips)
    setChipEditMode(false)
  }
  const cancelChipEdit = () => {
    skipDirtyCount.current += 1
    setEvolutionChips(chipSnapshot)
    setIsDirty(dirtyBeforeChipEdit.current)
    setChipEditMode(false)
  }
  const addChip = () => setEvolutionChips((prev) => [...prev, ''])
  const updateChip = (idx: number, val: string) =>
    setEvolutionChips((prev) => prev.map((c, i) => (i === idx ? val : c)))
  const deleteChip = (idx: number) => setEvolutionChips((prev) => prev.filter((_, i) => i !== idx))

  if (isLoading && !seriesData) return <TabSkeleton />

  const activeSerie = seriesData?.series.find((s) => s.serie_codigo === activeSerieCode)

  const seriesTabs =
    seriesData && seriesData.series.length > 0 ? (
      <div
        style={{
          display: 'flex',
          borderBottom: '2px solid #f1f3f5',
          marginTop: '12px',
          marginLeft: '-20px',
          marginRight: '-20px',
          paddingLeft: '20px',
        }}
      >
        {seriesData.series.map((serie) => (
          <button
            key={serie.serie_codigo}
            type='button'
            onClick={() => handleSerieChange(serie.serie_codigo)}
            style={{
              border: 'none',
              background: 'none',
              padding: '6px 20px',
              cursor: 'pointer',
              fontWeight: activeSerieCode === serie.serie_codigo ? 600 : 400,
              color:
                activeSerieCode === serie.serie_codigo ? 'var(--color-primary-blue)' : '#9ca3af',
              borderBottom:
                activeSerieCode === serie.serie_codigo
                  ? '2px solid var(--color-primary-blue)'
                  : '2px solid transparent',
              marginBottom: '-2px',
              fontSize: '14px',
              transition: 'color 0.15s',
            }}
          >
            {serie.serie_name}
          </button>
        ))}
      </div>
    ) : null

  const edicionActivoBadge = seriesData ? (
    <span
      style={{
        background: '#d4edda',
        color: '#155724',
        fontSize: '12px',
        padding: '2px 10px',
        borderRadius: 20,
        fontWeight: 600,
      }}
    >
      Edición activo
    </span>
  ) : null

  return (
    <div>
      {/* Header — Desempeño + series tabs */}
      <div className='card mt-10 p-5'>
        <div className='d-flex align-items-center gap-3 mt-2 mb-1'>
          <span className='text-gray-800 fs-2 fw-bold'>Desempeño</span>
          {edicionActivoBadge}
        </div>
        {seriesTabs}
      </div>

      {/* Mostrar información */}
      <div className='card mt-5 p-10'>
        <div className='d-flex align-items-center gap-3 mb-8'>
          <div className='form-check form-switch m-0'>
            <input
              className='form-check-input'
              type='checkbox'
              role='switch'
              checked={visibility}
              onChange={(e) => setVisibility(e.target.checked)}
            />
          </div>
          <label className='fw-semibold fs-6 text-gray-700 mb-0'>Mostrar información</label>
        </div>

        {/* Rentabilidad Anualizada */}
        <span className='fw-bold fs-4 text-gray-800 d-block mb-8'>Rentabilidad Anualizada</span>

        <label className='fw-bold fs-5 text-gray-800 mb-2'>Título</label>
        <input
          type='text'
          className='form-control mb-10'
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder='Evolución de Valor Cuota'
        />

        {/* Longitud de eje Y */}
        <label className='fw-bold fs-5 text-gray-800 mb-3'>Longitud de eje Y</label>
        <div className='d-flex align-items-center gap-3 flex-wrap mb-10'>
          <span className='text-gray-600' style={{ fontSize: 13 }}>
            Longitud inicial:
          </span>
          <input
            type='number'
            className='form-control'
            style={{ width: 110 }}
            value={yAxisMin}
            onChange={(e) => {
              if (MAX_TWO_DECIMALS_REGEX.test(e.target.value)) setYAxisMin(e.target.value)
            }}
            step={0.1}
            placeholder='0'
            disabled={!hasPermission(Permissions.FUND_TABS.QUOTE_VALUE.YAXIS_EDIT)}
          />
          <span className='text-gray-600' style={{ fontSize: 13 }}>
            Longitud final:
          </span>
          <input
            type='number'
            className='form-control'
            style={{ width: 110 }}
            value={yAxisMax}
            onChange={(e) => {
              if (MAX_TWO_DECIMALS_REGEX.test(e.target.value)) setYAxisMax(e.target.value)
            }}
            step={0.1}
            placeholder='0'
            disabled={!hasPermission(Permissions.FUND_TABS.QUOTE_VALUE.YAXIS_EDIT)}
          />
          <span className='text-gray-600' style={{ fontSize: 13 }}>
            Intervalos:
          </span>
          <input
            type='number'
            className='form-control'
            style={{ width: 110 }}
            value={yAxisInterval}
            onChange={(e) => {
              if (MAX_TWO_DECIMALS_REGEX.test(e.target.value)) setYAxisInterval(e.target.value)
            }}
            step={0.01}
            placeholder='0'
            disabled={!hasPermission(Permissions.FUND_TABS.QUOTE_VALUE.YAXIS_EDIT)}
          />
          <div className='d-flex align-items-center gap-2 ms-2'>
            <div className='form-check form-switch m-0'>
              <input
                id='yAxisStrict'
                className='form-check-input'
                type='checkbox'
                role='switch'
                checked={yAxisStrict}
                onChange={(e) => setYAxisStrict(e.target.checked)}
                disabled={!hasPermission(Permissions.FUND_TABS.QUOTE_VALUE.YAXIS_EDIT)}
              />
            </div>
            <label
              htmlFor='yAxisStrict'
              className='text-gray-600 mb-0'
              style={{ fontSize: 13, cursor: 'pointer' }}
            >
              Respetar límites
            </label>
          </div>
        </div>

        {/* Evolución del valor cuota — chips */}
        <label className='fw-bold fs-5 text-gray-800 mb-3'>Evolución del valor cuota</label>
        <div className='d-flex align-items-center gap-2 flex-wrap mb-3'>
          {evolutionChips.map((chip, idx) =>
            chipEditMode ? (
              <div key={idx} className='d-flex align-items-center gap-1'>
                <input
                  type='text'
                  className='form-control form-control-sm'
                  style={{ width: 80 }}
                  value={chip}
                  placeholder='ej. 3M'
                  autoFocus={idx === evolutionChips.length - 1 && chip === ''}
                  onChange={(e) => updateChip(idx, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') cancelChipEdit()
                  }}
                />
                <button
                  type='button'
                  onClick={() => deleteChip(idx)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    lineHeight: 1,
                  }}
                >
                  <i className='bi bi-x' style={{ fontSize: '0.85rem', color: '#ef4444' }} />
                </button>
              </div>
            ) : (
              <span
                key={idx}
                onClick={() =>
                  hasPermission(Permissions.FUND_TABS.QUOTE_VALUE.SHOW) && openChipModal(chip)
                }
                style={{
                  padding: '4px 10px',
                  borderRadius: 20,
                  border: '1px solid #dee2e6',
                  background: '#fff',
                  fontSize: 13,
                  display: 'inline-flex',
                  alignItems: 'center',
                  cursor: hasPermission(Permissions.FUND_TABS.QUOTE_VALUE.SHOW)
                    ? 'pointer'
                    : 'default',
                  opacity: hasPermission(Permissions.FUND_TABS.QUOTE_VALUE.SHOW) ? 1 : 0.5,
                }}
              >
                {chip}
              </span>
            )
          )}

          {/* Toolbar */}
          <div className='d-flex gap-1 ms-1'>
            {chipEditMode ? (
              <>
                <button
                  type='button'
                  className='btn btn-sm btn-custom-action'
                  style={{ padding: '4px 8px' }}
                  onClick={confirmChipEdit}
                  title='Confirmar'
                >
                  <i className='bi bi-check-lg' style={{ fontSize: '0.75rem' }} />
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-custom-action'
                  style={{ padding: '4px 8px' }}
                  onClick={addChip}
                  title='Agregar chip'
                >
                  <i className='bi bi-plus-lg' style={{ fontSize: '0.75rem' }} />
                </button>
                <button
                  type='button'
                  className='btn btn-sm btn-custom-action'
                  style={{ padding: '4px 8px' }}
                  onClick={cancelChipEdit}
                  title='Cancelar y revertir'
                >
                  <i className='bi bi-x-lg' style={{ fontSize: '0.75rem' }} />
                </button>
              </>
            ) : (
              <button
                type='button'
                className='btn btn-sm btn-custom-action'
                style={{ padding: '4px 8px' }}
                onClick={enterChipEditMode}
                disabled={!hasPermission(Permissions.FUND_TABS.QUOTE_VALUE.EVOLUTION_EDIT)}
                title='Editar chips'
              >
                <i className='bi bi-pencil' style={{ fontSize: '0.75rem' }} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Acciones */}
      {activeSerieCode &&
        hasPermission([
          Permissions.FUND_TABS.QUOTE_VALUE.SAVE,
          Permissions.FUND_TABS.QUOTE_VALUE.PUBLISH,
        ]) && (
          <div className='card mt-5 p-5'>
            <div className='d-flex justify-content-end gap-3'>
              {hasPermission(Permissions.FUND_TABS.QUOTE_VALUE.SAVE) && (
                <button
                  type='button'
                  className='btn btn-custom-action'
                  onClick={handleSave}
                  disabled={chipEditMode || quoteValueAction !== null}
                >
                  {quoteValueAction === 'saving' ? (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Guardando...
                      <span className='spinner-border spinner-border-sm align-middle ms-2' />
                    </span>
                  ) : (
                    'Guardar cambios'
                  )}
                </button>
              )}
              {hasPermission(Permissions.FUND_TABS.QUOTE_VALUE.PUBLISH) && (
                <button
                  type='button'
                  className='btn btn-custom-action'
                  onClick={handlePublish}
                  disabled={chipEditMode || quoteValueAction !== null}
                >
                  {quoteValueAction === 'publishing' ? (
                    <span className='indicator-progress' style={{ display: 'block' }}>
                      Publicando...
                      <span className='spinner-border spinner-border-sm align-middle ms-2' />
                    </span>
                  ) : (
                    'Publicar'
                  )}
                </button>
              )}
            </div>
          </div>
        )}

      {/* Modal evolución de valor cuota */}
      {modalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1050,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setModalOpen(false)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 12,
              width: '90%',
              maxWidth: 480,
              maxHeight: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className='d-flex align-items-center gap-3 px-5 pt-4 pb-3'>
              <span className='fw-bold fs-5 text-gray-800'>Evolución de valor cuota</span>
              {modalFilter && (
                <span
                  style={{
                    background: 'var(--color-primary-blue)',
                    color: '#fff',
                    borderRadius: 20,
                    padding: '2px 12px',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  {modalFilter}
                </span>
              )}
              <div className='d-flex align-items-center gap-2 ms-auto'>
                <div style={{ position: 'relative' }}>
                  <DateInput
                    className='form-control form-control-sm'
                    style={{ width: 130, paddingRight: 28 }}
                    value={modalDateSearch}
                    onChange={(val) => setModalDateSearch(val)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        setModalPage(1)
                        fetchModalData(1, modalFilter, modalDateSearch)
                      }
                    }}
                  />
                  <i
                    className='bi bi-search'
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9ca3af',
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setModalPage(1)
                      fetchModalData(1, modalFilter, modalDateSearch)
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Serie name subtitle */}
            {activeSerie && (
              <div className='text-center fw-bold fs-6 text-gray-700 py-2'>
                {activeSerie.serie_name}
              </div>
            )}

            {/* Contenido */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '0 20px 12px' }}>
              {modalLoading ? (
                <div className='text-center py-5'>
                  <span className='spinner-border spinner-border-sm text-primary' />
                </div>
              ) : modalRows.length === 0 ? (
                <div className='text-center text-muted py-4' style={{ fontSize: 13 }}>
                  Sin datos disponibles
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {modalRows.map((row, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8 }}>
                      <span
                        style={{
                          flex: 1,
                          border: '1px solid #e5e7eb',
                          borderRadius: 8,
                          padding: '8px 12px',
                          fontSize: 13,
                          color: '#6b7280',
                          textAlign: 'center',
                        }}
                      >
                        {row.quote_date.split('-').reverse().join('/')}
                      </span>
                      <span
                        style={{
                          flex: 1,
                          border: '1px solid #e5e7eb',
                          borderRadius: 8,
                          padding: '8px 12px',
                          fontSize: 13,
                          color: '#6b7280',
                          textAlign: 'center',
                        }}
                      >
                        {row.quote_value ?? '—'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Paginación */}
            {modalTotalPages > 1 && (
              <div className='py-3'>
                <Pagination
                  page={modalPage}
                  totalPages={modalTotalPages}
                  onChange={(p) => setModalPage(p)}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {isModalOpen && (
        <UnsavedChangesModal
          onLeave={handleLeave}
          onCancel={handleCancel}
          onSave={handleSaveAndContinue}
          saving={saving}
        />
      )}
    </div>
  )
}
